// Carrega e inicializa o mermaid uma única vez (import dinâmico — fora do bundle inicial), e
// cacheia o SVG resultante por código-fonte — usado tanto pelo componente `DiagramaMermaid`
// (renderização normal, ao vivo) quanto pelo pré-render disparado pelo hook antes de "revelar"
// uma mensagem de streaming que contém diagrama (evita o usuário ver a mensagem já completa,
// mas com o(s) diagrama(s) ainda carregando/"pulando" pra dentro alguns instantes depois).
let mermaidPromise: Promise<typeof import('mermaid').default> | null = null;
let contadorId = 0;
const cacheSvgPorCodigo = new Map<string, Promise<string>>();

function carregarMermaid() {
    if (!mermaidPromise) {
        mermaidPromise = import('mermaid').then((mod) => {
            mod.default.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'neutral' });
            return mod.default;
        });
    }
    return mermaidPromise;
}

// Tema do diagrama, ancorado na cor de marca (#155dfc). Deixa a TELA mais polida (fundo/borda/
// linha) e NUNCA substitui as cores que o modelo aplica por classDef — só define o que sobra sem
// classe explícita. Aplicado via diretiva `%%{init: ...}%%` inline no próprio código (Mermaid lê
// isso da primeira linha), não via `mermaid.initialize()` global, para não afetar outros usos.
//
// Vale em TODOS os níveis desde 2026-07-25. Antes era exclusivo do Arquiteto 3.0 — que é
// `bloqueado: true` no seletor (ver modelos.ts), então na prática o tema nunca chegava à tela e
// todo diagrama saía no cinza do tema `neutral`. A instrução de colorir por categoria também
// mudou de lugar no backend (de SecaoNivelUgarquiArquiteto3 para BLOCOS ESPECIAIS/mermaid).
const DIRETIVA_TEMA =
    "%%{init: {'theme':'base','themeVariables':{'primaryColor':'#EFF6FF','primaryTextColor':" +
    "'#111827','primaryBorderColor':'#155dfc','lineColor':'#6B7280','tertiaryColor':'#F9FAFB'}}}%%\n";

/** Renderiza (ou reaproveita do cache) o SVG do código Mermaid informado. Nunca rejeita mais de
 * uma vez por código — chamadas repetidas com o mesmo código reusam a MESMA Promise em voo. */
export function renderizarMermaid(codigo: string): Promise<string> {
    const chaveCache = codigo;
    const emCache = cacheSvgPorCodigo.get(chaveCache);
    if (emCache) {
        return emCache;
    }

    const idUnico = `ugarqui-mermaid-${(contadorId += 1)}`;
    const codigoFinal = DIRETIVA_TEMA + codigo;
    const promise = carregarMermaid()
        .then((mermaid) => mermaid.render(idUnico, codigoFinal))
        .then(({ svg }) => svg)
        .finally(() => removerSandboxOrfao(idUnico));

    cacheSvgPorCodigo.set(chaveCache, promise);
    // Se a renderização falhar (sintaxe inválida), remove do cache — não queremos "lembrar" uma
    // falha permanentemente caso o mesmo código apareça de novo por engano.
    promise.catch(() => cacheSvgPorCodigo.delete(chaveCache));
    return promise;
}

/** `mermaid.render` (sem `container` explícito) cria um elemento temporário só pra medir o SVG,
 * anexado direto em `document.body`, e remove sozinho no caminho de sucesso — mas em erro de
 * sintaxe (comum: diagrama malformado gerado pelo modelo) esse cleanup não acontece, deixando o
 * node de erro preso no body pra sempre. Um a cada diagrama invalido, acumulando: vaza
 * visualmente atrás do widget (sem o CSS do mermaid, aparece como texto solto no fluxo normal da
 * página) e pesa o DOM/layout da página inteira conforme a conversa cresce. Varre por qualquer
 * elemento cujo id contenha o id único desta renderização — mermaid prefixa esse id de formas
 * diferentes conforme a versão (`d`/`i` + id), então o match é por substring, não por prefixo
 * exato. */
function removerSandboxOrfao(idUnico: string): void {
    document.querySelectorAll(`[id*="${idUnico}"]`).forEach((elemento) => elemento.remove());
}

const REGEX_BLOCO_MERMAID = /```mermaid\n([\s\S]*?)```/g;

/** Extrai o código-fonte de todo bloco ```mermaid``` presente no texto (markdown) informado. */
export function extrairCodigosMermaid(textoMarkdown: string): string[] {
    const codigos: string[] = [];
    REGEX_BLOCO_MERMAID.lastIndex = 0;
    let resultado: RegExpExecArray | null;
    while ((resultado = REGEX_BLOCO_MERMAID.exec(textoMarkdown)) !== null) {
        codigos.push(resultado[1]);
    }
    return codigos;
}
